import { PageLayout } from "../components/ui/PageLayout.js";
import { Section } from "../components/ui/Section.js";
import { Switch } from "../components/ui/Switch.js";
import {
  type StrategySetting,
  useSetStrategyEnabled,
  useStrategySettings,
} from "../lib/settings.js";

const FAMILY_LABEL: Record<string, string> = {
  intraday: "Intraday",
  rank: "Rank",
  technical: "Technical",
  swing: "Swing",
  event: "Event",
};

/** Groups in the order the API sent them, so the page order is the catalogue's. */
function byFamily(strategies: readonly StrategySetting[]): [string, StrategySetting[]][] {
  const groups = new Map<string, StrategySetting[]>();
  for (const strategy of strategies) {
    const group = groups.get(strategy.family) ?? [];
    group.push(strategy);
    groups.set(strategy.family, group);
  }
  return [...groups];
}

/**
 * One strategy, as a ruled row rather than a card.
 *
 * The signal types are mono because they are database values; the label and
 * description are prose. See docs/design.md, "Typography".
 */
function StrategyRow({ strategy }: { strategy: StrategySetting }) {
  const setEnabled = useSetStrategyEnabled();

  return (
    <li className="flex items-start gap-6 border-b border-border/50 py-2">
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-text">{strategy.label}</span>
        <span className="text-body-sm text-text-muted">{strategy.description}</span>
        <span className="text-data-sm font-data text-text-muted tabular-nums">
          {strategy.signalTypes.join(", ")}
        </span>
      </div>
      <div className="ml-auto pt-1">
        <Switch
          checked={strategy.enabled}
          label={strategy.label}
          onCheckedChange={(enabled) => setEnabled.mutate({ strategy: strategy.key, enabled })}
        />
      </div>
    </li>
  );
}

export function SettingsPage() {
  const settings = useStrategySettings();

  return (
    <PageLayout title="Settings">
      <p className="max-w-prose text-body-sm text-text-muted">
        Switching a strategy off stops it writing new signals and hides the ones it already wrote,
        for everybody.
      </p>

      {settings.isPending && <p className="text-body-sm text-text-muted">Loading strategies…</p>}
      {settings.isError && (
        <p className="text-body-sm text-danger">Could not load strategies. Reload to try again.</p>
      )}
      {settings.data &&
        byFamily(settings.data.strategies).map(([family, strategies]) => (
          <Section key={family} title={FAMILY_LABEL[family] ?? family}>
            <ul className="flex flex-col">
              {strategies.map((strategy) => (
                <StrategyRow key={strategy.key} strategy={strategy} />
              ))}
            </ul>
          </Section>
        ))}
    </PageLayout>
  );
}
